import React, { useEffect, useRef } from 'react';
import { Alert, Collapse, Snackbar } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import WifiIcon from '@mui/icons-material/Wifi';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';

/**
 * Sticky offline banner at the top of the page and a brief "back online"
 * snackbar when the connection is restored.
 */
export const OfflineBanner: React.FC = () => {
  const { isConnected } = useNetworkStatus();
  const prevConnected = useRef<boolean | null>(null);
  const [showBackOnline, setShowBackOnline] = React.useState(false);

  useEffect(() => {
    if (prevConnected.current === null) {
      prevConnected.current = isConnected;
      return;
    }
    if (isConnected && !prevConnected.current) {
      setShowBackOnline(true);
    }
    prevConnected.current = isConnected;
  }, [isConnected]);

  return (
    <>
      {/* Sticky offline warning — collapses away when online */}
      <Collapse in={!isConnected} unmountOnExit>
        <Alert
          severity="error"
          icon={<WifiOffIcon fontSize="small" />}
          sx={{
            borderRadius: 0,
            position: 'sticky',
            top: 0,
            zIndex: 1300,
            py: 0.5,
            fontWeight: 500,
          }}
        >
          No internet connection — data shown may be outdated. Changes will sync when you reconnect.
        </Alert>
      </Collapse>

      {/* Brief "back online" snackbar */}
      <Snackbar
        open={showBackOnline}
        autoHideDuration={3000}
        onClose={() => setShowBackOnline(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          icon={<WifiIcon fontSize="small" />}
          onClose={() => setShowBackOnline(false)}
          sx={{ width: '100%' }}
        >
          Back online — syncing data…
        </Alert>
      </Snackbar>
    </>
  );
};
